/**
* 评分图表组件
* 显示雷达图形式的评分
*
* @component ScoreEchart
* @props {Array} value - 评分数组
* @props {Array} indicator - 维度指标数组
* @props {Array} maxScores - 每个维度的满分值数组
*/

<template>
  <div class="score-echart">
    <div class="score-echart-title" v-if="total">
      <strong>{{ total }}</strong>
      整体得分
    </div>
    <Echarts v-if="isDataValid" :option="option" style="height: 100%" />
    <div v-else class="loading-container">
      <el-icon class="loading-icon"><Loading /></el-icon>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { Loading } from '@element-plus/icons-vue'
import Echarts from 'vue-echarts'
import * as echarts from 'echarts'

const props = defineProps({
  value: {
    type: Array,
    required: true
  },
  indicator: {
    type: Array,
    required: true
  },
  maxScores: {
    type: Array,
    required: true
  }
})

// 检查数据是否有效
const isDataValid = computed(() => {
  return props.value?.length > 0 &&
      props.indicator?.length > 0 &&
      props.maxScores?.length > 0
})

const total = computed(() => {
  if (!isDataValid.value) return 0;
  return props.value.reduce((sum, current) => sum + current, 0)
})

const option = computed(() => {
  if (!isDataValid.value) {
    return {
      radar: {
        indicator: [{ name: '加载中...', max: 40 }]
      }
    }
  }

  const indicators = props.indicator.map((name, index) => ({
    name,
    max: props.maxScores[index]
  }))

  return {
    color: ['#00A160'],
    tooltip: {
      trigger: 'item'
    },
    radar: {
      radius: '90%',
      center: ['50%', '65%'],
      splitNumber: 3,
      name: {
        color: '#222',
        formatter: (value) => {
          if (value.length <= 4) {
            return `{a|${value}}`
          } else if (value.length === 5) {
            return `{b|${value}}`
          } else if (value.length === 6) {
            return `{c|${value}}`
          } else if (value.length === 7) {
            return `{d|${value}}`
          } else if (value.length === 8) {
            return `{e|${value}}`
          } else if (value.length === 9) {
            return `{f|${value}}`
          } else if (value.length === 10) {
            return `{g|${value}}`
          }
        },
        textStyle: {
          rich: {
            a: {
              padding: [-10, -35]
            },
            b: {
              padding: [-10, -40]
            },
            c: {
              padding: [-10, -45]
            },
            d: {
              padding: [-10, -50]
            },
            e: {
              padding: [-10, -65]
            },
            f: {
              padding: [-10, -70]
            },
            g: {
              padding: [-10, -75]
            }
          }
        }
      },
      indicator: indicators,
      splitArea: {
        areaStyle: {
          color: 'transparent',
        },
      },
      splitLine: {
        lineStyle: {
          color: 'rgba(0,82,16,0.25)',
        },
      },
      axisLine: {
        lineStyle: {
          color: 'rgba(0,82,16,0.25)',
        },
      },
    },
    series: [
      {
        name: '得分',
        type: 'radar',
        emphasis: {
          lineStyle: {
            width: 4
          }
        },
        data: [
          {
            value: props.value,
            name: '得分',
            symbolSize: 6,
            lineStyle: {
              width: 2
            },
            areaStyle: {
              opacity: 0.1
            }
          }
        ]
      }
    ]
  }
})
</script>

<style lang="scss" scoped>
.score-echart {
  display: flex;
  align-items: center;
  height: 170px;
  border-radius: 6px;
  background: linear-gradient(270deg, #E7FFF5 0%, #F4FFFB 100%);
  .score-echart-title {
    width: 30%;
    font-size: 12px;
    text-align: center;
    strong {
      display: block;
      margin-bottom: 6px;
      font-size: 32px;
      color: var(--el-color-primary);
    }
  }
  :deep(.echarts) {
    width: 70%;
    height: 100%;
  }
}

.loading-container {
  width: 70%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;

  .loading-icon {
    font-size: 24px;
    color: var(--el-color-primary);
    animation: rotating 2s linear infinite;
  }
}

@keyframes rotating {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>